import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { BehaviorSubject, Observable, Subject, lastValueFrom, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { AuthService, AuthType } from "./auth.service";
import { environment } from "../../../environments/environment";
import { map } from "rxjs/operators";
import { Meta } from "@angular/platform-browser";
import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Addon } from "../models/addon.model";
import { LoadingService } from "../components/loading/loading.service";

export interface AppAppearanceData {
  theme: ColorTheme;
}

export const enum ColorTheme {
  DefaultGreen = 1,
  PersianGreen,
  PinkishOrange,
  Lipstick,
  YellowishOrange,
  Azure,
  LightishBlue,
  DodgerBlue,
  BlueBayoux,
  Black,
  Golden,
  LightGreen,
  CustomColor
}

export let THEMES_COLORS_MAP = {
  [ColorTheme.DefaultGreen]: { colors: ["#6cb33f", "#a0da6c"], orderNumber: 2, index: 0 },
  [ColorTheme.PersianGreen]: { colors: ["#008657", "#008657"], orderNumber: 1, index: 1 },
  [ColorTheme.PinkishOrange]: { colors: ["#f26648", "#f26648"], orderNumber: 9, index: 2 },
  [ColorTheme.Lipstick]: { colors: ["#111E6C", "#111E6C"], orderNumber: 4, index: 3 },
  [ColorTheme.YellowishOrange]: { colors: ["#f8971d", "#f8971d"], orderNumber: 10, index: 4 },
  [ColorTheme.Azure]: { colors: ["#00aeef", "#00aeef"], orderNumber: 6, index: 5 },
  [ColorTheme.LightishBlue]: { colors: ["#3A7ABE", "#3A7ABE"], orderNumber: 5, index: 6 },
  [ColorTheme.DodgerBlue]: { colors: ["#6666cc", "#6666cc"], orderNumber: 7, index: 7 },
  [ColorTheme.BlueBayoux]: { colors: ["#525f81", "#525f81"], orderNumber: 8, index: 8 },
  [ColorTheme.Black]: { colors: ["#303030", "#303030"], orderNumber: 12, index: 9 },
  [ColorTheme.Golden]: { colors: ["#b68a35", "rgba(182, 138, 53, 0.6)"], orderNumber: 11, index: 10 },
  [ColorTheme.LightGreen]: { colors: ["#91C740", "#91C740"], orderNumber: 3, index: 11 },
  [ColorTheme.CustomColor]: { colors: [], orderNumber: -1, index: 13 },

};

export const APP_APPEARANCE_ADDON_NAME = "App Appearance";

@Injectable({
  providedIn: "root"
})
export class AppAppearanceService {
  private cachedAppAppearance: { [groupId: string]: AppAppearanceData } = {};
  pageWidth$: BehaviorSubject<string> = new BehaviorSubject<string>('default');
  private appAppearanceAddon$: Observable<Addon>;
  loadAppAppearanceAddon$: Observable<Addon>;

  constructor(private http: HttpClient,
    private authService: AuthService,
    private meta: Meta,
    private loadingService: LoadingService,
    @Inject(DOCUMENT) public document: Document,
    @Inject(PLATFORM_ID) public platformId,
  ) { 
    this.authService.getAppAppearanceData().subscribe((appAppearanceAddon) => {
      this.applyAppAppearanceContentWidth(appAppearanceAddon);
    });
  }

  public configureThemeColorsFromConfig() {
    if (environment['themeColors']) {
      Object.keys(environment['themeColors']).forEach(k => {
        const theme = Object.values(THEMES_COLORS_MAP).find(c => c.orderNumber === Number(k));
        if (theme) {
          theme.colors = [environment['themeColors'][k], environment['themeColors'][k]]
        }
      })
    }
  }

  updateCustomColor(hexColor: string): void {
    // console.log("Received hex color:", hexColor);

    if (/^#[0-9A-F]{6}$/i.test(hexColor.trim())) {
      THEMES_COLORS_MAP[ColorTheme.CustomColor].colors = [hexColor, hexColor];
      // console.log('Custom color updated:', THEMES_COLORS_MAP[ColorTheme.CustomColor].colors);
    } else {
      // console.error('Invalid hex color');
    }
  }

  getAppAppearanceData(groupId: string): Observable<AppAppearanceData> {
    if (this.cachedAppAppearance[groupId]) {
      return of(this.cachedAppAppearance[groupId]);
    }
    return this.authService.getAddonsAuth(this.authService.idenediGroupId ? this.authService.idenediGroupId : groupId).pipe(
      map((addons = []) => {
        const appearanceAddon = addons.find(
          a => a.Type === APP_APPEARANCE_ADDON_NAME
        );
        if (!appearanceAddon) {
          return null;
        }
        const orgId = Number(appearanceAddon.PublicProperties?.organziationId);
        // If org Id is
        if (
          orgId >
          Object.keys(THEMES_COLORS_MAP).length
        ) {
          return {
            theme: ColorTheme.DefaultGreen
          };
        }
        return {
          theme:
            orgId ||
            ColorTheme.DefaultGreen
        };
      })
    );
  }

  getHexColorData() : string {
    let hexColor;
    this.authService
    .getAddonsAuth(this.authService.idenediGroupId)
    .subscribe((addons) => {
      let appAppearanceAddon = addons?.find(item => item.Type === AuthType.AppAppearance);
      hexColor = appAppearanceAddon.PublicProperties?.hexColor;
    });
    // console.log("Step 6. I have retrived the color")
    // console.log("Which is...,",hexColor)
    return hexColor;
  }

  setColorTheme(theme: ColorTheme): void {
    // console.log("Step 3.Setting theme color.")
    // console.log("which is...",theme)
    let themeColors;
    // Set Hex color is theme is -1
    if(theme == -1 as unknown as ColorTheme) {
      // console.log("Step 4. I am custom and need to set color to the custom color array.")
      // console.log("Step 5. But first need to get the color.")
      let customHexColor = this.getHexColorData();
      if(customHexColor){
      // Update hex color in custom color arrays colors
      this.updateCustomColor(customHexColor);
      }
      themeColors = THEMES_COLORS_MAP[13];
    } else {
      // Note: theme is basically, the order number
      themeColors = THEMES_COLORS_MAP[theme];
    }
    // console.log("Step 7. Current themeColor data noew iss...",themeColors)
    this.meta.updateTag({
      name: 'theme-color',
      content: themeColors.colors[0]
    })
    if (isPlatformBrowser(this.platformId)) {

      this.document.documentElement.style.setProperty(
        "--primary-color",
        themeColors.colors[0]
      );
      this.document.documentElement.style.setProperty(
        "--primary-color-rgb",
        this.hexToRgb(themeColors.colors[0])
      );
      this.document.documentElement.style.setProperty(
        "--primary-color-lighter",
        themeColors.colors[1]
      );
      this.setContrastBg(themeColors.colors[0]);
    }


  }
  private hexToRgb(hex: string) {
    const result: any = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
  }

  setPageWidth(width) {
    this.pageWidth$.next(width);
  }

  public setDefaultThemeColor(){
    // console.log("Step 2. Getting default theme color.")
    if(!this.authService.idenediGroupId){
      return lastValueFrom(of(false));
    }
    const req$ =  this.getAppAppearanceData(this.authService.idenediGroupId).pipe(
      map((appAppearance) => {
        // console.log("Which is...",appAppearance)
        //Theme is organization ID
        const theme = appAppearance?.theme || ColorTheme.DefaultGreen;
        this.setColorTheme(theme);
        return true;
      })
    );
    return lastValueFrom(req$);
  }
  setContrastBg(primaryColor = "#ffff") {


    const hex = primaryColor.replace('#', '');
    const fullHex = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;

    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);

    const rgba = `rgba(${r}, ${g}, ${b}, 0.1)`;
    this.document.documentElement.style.setProperty(
      "--contrast-background",
      rgba
    );
  }

  applyAppAppearanceContentWidth(appearanceData: any) {
    const isFullWidth = appearanceData?.fullScreenWidthOnDesktop === '1';
    const sizeOnDesktop = Number(appearanceData?.fullScreenSizeOnDesktop) || 1160;
    const value = isFullWidth ? (sizeOnDesktop / 16) + 'rem' : '72.5rem';
    // this.setPageWidth(isFullWidth ? 'full' : 'default');

    const root = document.documentElement;

    // Normal variables
    root.style.setProperty('--max-content-width', value);
    root.style.setProperty('--max-full-content-width', value);

    // With !important
    root.style.setProperty('--container-width', value, 'important');
    root.style.setProperty('--container-width-large', value, 'important');

    const englishFont = appearanceData?.englishFont || 'Lato';
    const arabicFont = appearanceData?.arabicFont || 'Almarai';
    const fontSizeIncrease = Number(appearanceData?.fontSizeIncrease) || 0;

    root.style.setProperty('--xcdr-english-font', `'${englishFont}'`);
    root.style.setProperty('--xcdr-arabic-font', `'${arabicFont}'`);
    root.style.setProperty('--rate-of-font-size-change-percent', String(fontSizeIncrease));

    this.loadGoogleFont(englishFont, 'xcdr-english-font-link');
    this.loadGoogleFont(arabicFont, 'xcdr-arabic-font-link');
  }

  private readonly GOOGLE_FONT_URLS: Record<string, string> = {
    'Lato':                  'https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&display=swap',
    'Roboto':                'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700;900&display=swap',
    'Open Sans':             'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700;900&display=swap',
    'Montserrat':            'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap',
    'Inter':                 'https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap',
    'Calibri':               'https://fonts.cdnfonts.com/css/calibri-2',
    'Almarai':               'https://fonts.googleapis.com/css2?family=Almarai:wght@300;400;700;800&display=swap',
    'IBM Plex Sans Arabic':  'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@400;700&display=swap',
    'Cairo':                 'https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;900&display=swap',
    'Tajawal':               'https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;900&display=swap',
    'Noto Sans Arabic':      'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;700;900&display=swap',
    'Dubai':                 'https://fonts.cdnfonts.com/css/dubai',
  };

  private loadGoogleFont(fontName: string, linkId: string): void {
    const url = this.GOOGLE_FONT_URLS[fontName];
    if (!url || !isPlatformBrowser(this.platformId)) { return; }

    let link = this.document.getElementById(linkId) as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      this.document.head.appendChild(link);
    }

    if (link.href !== url) {
      link.href = url;
    }
  }
}
